<template>
    <div class="space-y-3 text-left">
        <!-- Search Input (rounded-full style for prominent) -->
        <div
            class="relative flex items-center bg-white dark:bg-surface-card"
            :class="prominentSearch
                ? 'rounded-full border border-gray-300 dark:border-default'
                : 'rounded-xl border border-gray-200 dark:border-default'"
        >
            <input
                v-model="searchQuery"
                type="text"
                class="block w-full border-transparent bg-transparent transition-colors focus:outline-none focus:border-transparent focus:ring-0 dark:text-white dark:placeholder-txt-body text-gray-900"
                :class="prominentSearch
                    ? 'h-[52px] rounded-full py-4 pl-5 pr-24 text-lg placeholder-gray-400 dark:placeholder-txt-body'
                    : 'rounded-lg py-2.5 pl-4 pr-16 text-sm placeholder-gray-500 dark:placeholder-txt-body'"
                :placeholder="placeholder"
                @keydown.enter="submitSearch"
                @input="debouncedSearch"
            >
            <div class="absolute inset-y-0 right-0 flex items-center gap-2" :class="prominentSearch ? 'pr-1.5' : 'pr-2'">
                <!-- Clear button -->
                <button
                    v-if="searchQuery"
                    type="button"
                    class="text-gray-400 dark:text-txt-body hover:text-gray-600 dark:hover:text-txt-body"
                    @click="clearSearch"
                >
                    <CIcon name="close" :class="prominentSearch ? 'size-5' : 'size-4'" />
                </button>
                <!-- Search button -->
                <button
                    type="button"
                    class="flex items-center justify-center rounded-full bg-gray-200 dark:bg-subtle hover:bg-gray-300 dark:hover:bg-subtle transition-colors shrink-0"
                    :class="prominentSearch ? 'size-10' : 'size-8'"
                    @click="submitSearch"
                >
                    <CIcon name="search" :class="prominentSearch ? 'size-5 text-gray-500 dark:text-txt-body' : 'size-4 text-gray-400 dark:text-txt-body'" />
                </button>
            </div>
        </div>

        <!-- Active Filters Chips -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 dark:border-primary-900/50 dark:bg-primary-900/20">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-txt-body">Aktívne:</span>

            <!-- Search query chip -->
            <button
                v-if="searchQuery"
                class="group inline-flex min-h-8 items-center gap-1 rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white transition-all hover:bg-primary-700"
                @click="clearSearch"
            >
                <CIcon name="search" class="size-3" />
                "{{ searchQuery }}"
                <CIcon name="close" class="size-3 opacity-75 group-hover:opacity-100" />
            </button>

            <!-- Dynamic filter chips -->
            <button
                v-for="chip in activeFilterChips"
                :key="chip.key"
                class="group inline-flex min-h-8 items-center gap-1 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white transition-all hover:bg-gray-900 dark:bg-subtle dark:hover:bg-subtle"
                @click="clearFilter(chip.key, chip.value)"
            >
                {{ chip.label }}
                <CIcon name="close" class="size-3 opacity-75 group-hover:opacity-100" />
            </button>

            <!-- Clear all button. FilterToggleRow below has its own whenever it is shown. -->
            <button
                v-if="!filterSections.length"
                class="ml-auto inline-flex min-h-8 items-center gap-1 rounded-full border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-all hover:border-gray-400 hover:dark:border-default dark:border-default dark:bg-transparent dark:text-txt-body dark:hover:bg-surface-card"
                @click="resetAll"
            >
                <Icon name="heroicons:x-circle" class="size-4" />
                Vymazať
            </button>
        </div>

        <!-- Quick Filter Tags (if provided) -->
        <div v-if="quickFilters.length > 0" class="flex flex-wrap gap-2">
            <button
                v-for="filter in quickFilters"
                :key="Array.isArray(filter.value) ? filter.value.join('|') : filter.value"
                class="rounded-full border px-3 py-1.5 text-sm font-medium transition-all"
                :class="isQuickFilterActive(filter)
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 dark:border-default dark:bg-surface-card dark:text-txt-body'"
                @click="toggleQuickFilter(filter)"
            >
                {{ filter.label }}
            </button>
        </div>

        <!-- Search Suggestions (centered, full-width like blog tags) -->
        <div v-if="searchSuggestions.length > 0 && !searchQuery" class="flex flex-wrap justify-center gap-2">
            <span class="text-sm text-gray-500 dark:text-txt-body self-center mr-1">Skús napríklad:</span>
            <button
                v-for="suggestion in searchSuggestions"
                :key="suggestion.query"
                class="px-4 py-2 rounded-full text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-subtle dark:text-txt-body dark:hover:bg-subtle"
                @click="applySuggestion(suggestion)"
            >
                {{ suggestion.label }}
            </button>
        </div>

        <!-- Filter Toggle + Results count row -->
        <FilterToggleRow
            v-if="filterSections.length > 0"
            v-model:show-filters="showFilters"
            :has-active-filters="hasActiveFilters"
            :filter-count="activeFilterCount"
            @reset="resetAll"
        >
            <slot name="results-count" />
        </FilterToggleRow>

        <!-- Collapsible Filter Panel -->
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="showFilters" class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-default dark:bg-surface-card/40">
                <!-- Dynamic filter sections -->
                <div class="grid gap-4 overflow-visible md:grid-cols-2 lg:grid-cols-3">
                    <slot name="filters" :update="updateFilter" :value="modelValue" :options="filterOptions" />

                    <!-- Default filter sections based on config -->
                    <template v-if="!$slots.filters">
                        <div
                            v-for="section in visibleFilterSections"
                            :key="section.key"
                            :class="[
                                isDropdownSection(section)
                                    ? 'overflow-visible'
                                    : 'filter-section overflow-visible rounded-lg border border-gray-200 bg-white p-3 dark:border-default dark:bg-surface-card/40',
                                section.displayAs === 'keywords' ? 'md:col-span-2 lg:col-span-3' : ''
                            ]"
                        >
                            <!-- Checkbox list display using design system component -->
                            <div v-if="section.displayAs === 'checkboxes'">
                                <p class="mb-2 text-sm text-gray-500 dark:text-txt-body">
                                    {{ section.placeholder }}
                                </p>
                                <CFormCheckboxGroup
                                    :model-value="getFilterValue(section.key)"
                                    :options="filterOptions[section.key] || []"
                                    option-label-key="label"
                                    option-value-key="id"
                                    option-disabled-key="disabled"
                                    @update:model-value="updateFilter(section.key, $event?.length ? $event : undefined)"
                                />
                            </div>

                            <!-- Searchable checkbox list (inline, no dropdown click needed) -->
                            <div v-else-if="section.displayAs === 'searchable-list'">
                                <CFormInput
                                    v-model="filterSearchQueries[section.key]"
                                    class="mb-2"
                                    :placeholder="section.placeholder"
                                />
                                <div class="max-h-72 overflow-y-auto">
                                    <CFormCheckboxGroup
                                        :model-value="getFilterValue(section.key)"
                                        :options="getFilteredOptions(section.key)"
                                        option-label-key="label"
                                        option-value-key="id"
                                        option-disabled-key="disabled"
                                        @update:model-value="updateFilter(section.key, $event?.length ? $event : undefined)"
                                    />
                                </div>
                            </div>

                            <!-- Keywords autocomplete (same UX as project filters) -->
                            <div v-else-if="section.displayAs === 'keywords'">
                                <CFormAutocomplete
                                    autoexpand
                                    :disabled="isLoading"
                                    no-results-message="Žiadne výsledky pre zadané kľúčové slová"
                                    :options="getKeywordOptions(section.key)"
                                    option-value-key="value"
                                    option-label-key="label"
                                    :placeholder="section.placeholder"
                                    :model-value="getFilterValue(section.key)"
                                    @update:model-value="updateFilter(section.key, $event?.length ? $event : undefined)"
                                />
                            </div>

                            <!-- Default dropdown display -->
                            <FilterDropdownNext
                                v-else
                                :disabled="isLoading"
                                :options="filterOptions[section.key] || []"
                                :placeholder="section.placeholder"
                                searchable
                                :value="modelValue[section.key]"
                                @input="updateFilter(section.key, $event)"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import debounce from 'lodash-es/debounce'
import { matchSorter } from 'match-sorter'
import FilterDropdownNext from '@/components/filters/components/FilterDropdownNext.vue'

export interface FilterSection {
    key: string
    label: string
    icon: string
    placeholder: string
    hidden?: boolean
    displayAs?: 'checkboxes' | 'searchable-list' | 'dropdown' | 'keywords'
}

export interface QuickFilter {
    label: string
    key: string
    value: string | string[]
}

export interface SearchSuggestion {
    label: string
    query: string
}

export interface FilterChip {
    key: string
    value: string
    label: string
}

const props = withDefaults(defineProps<{
    modelValue: Record<string, any>
    placeholder?: string
    collection?: 'jobs' | 'mentors' | 'skills' | 'projects' | 'volunteers'
    filterSections?: FilterSection[]
    quickFilters?: QuickFilter[]
    searchSuggestions?: SearchSuggestion[]
    filterOptions?: Record<string, any[]>
    isLoading?: boolean
    hiddenFilters?: string[]
    prominentSearch?: boolean
}>(), {
    placeholder: 'Čo hľadáš?',
    collection: 'jobs',
    filterSections: () => [],
    quickFilters: () => [],
    searchSuggestions: () => [],
    filterOptions: () => ({}),
    isLoading: false,
    hiddenFilters: () => [],
    prominentSearch: false
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: Record<string, any>): void
    (e: 'search', query: string): void
}>()

const showFilters = ref(false)
const searchQuery = ref('')
const filterSearchQueries = ref<Record<string, string>>({})

// Initialize search query from model value
watch(() => props.modelValue.q, (newQ) => {
    if (newQ !== searchQuery.value) {
        searchQuery.value = newQ || ''
    }
}, { immediate: true })

// Debounced search
const debouncedSearch = debounce(() => {
    emit('search', searchQuery.value)
}, 400)

function submitSearch() {
    emit('search', searchQuery.value)
}

function clearSearch() {
    searchQuery.value = ''
    emit('search', '')
}

// Filter logic
const visibleFilterSections = computed(() => {
    return props.filterSections.filter(s => !s.hidden && !props.hiddenFilters.includes(s.key))
})

const hasActiveFilters = computed(() => {
    const { q, page, ...rest } = props.modelValue
    return !!searchQuery.value || Object.keys(rest).some(key => {
        const val = rest[key]
        return Array.isArray(val) ? val.length > 0 : !!val
    })
})

const activeFilterCount = computed(() => {
    let count = 0
    Object.entries(props.modelValue).forEach(([key, value]) => {
        if (key === 'q' || key === 'page') return
        if (Array.isArray(value)) count += value.length
        else if (value) count++
    })
    return count
})

const activeFilterChips = computed((): FilterChip[] => {
    const chips: FilterChip[] = []

    Object.entries(props.modelValue).forEach(([key, value]) => {
        if (key === 'q' || key === 'page') return

        if (Array.isArray(value)) {
            value.forEach(v => {
                const option = props.filterOptions[key]?.find((o: any) => o.id === v)
                chips.push({
                    key,
                    value: v,
                    label: formatFilterChipLabel(key, option?.label || String(v))
                })
            })
        } else if (value) {
            const option = props.filterOptions[key]?.find((o: any) => o.id === value)
            chips.push({
                key,
                value: value as string,
                label: formatFilterChipLabel(key, option?.label || String(value))
            })
        }
    })

    return chips
})

function formatFilterChipLabel(key: string, label: string) {
    const isTagLikeFilter = ['topics', 'interests'].includes(key)
    if (!isTagLikeFilter) return label
    return label.startsWith('#') ? label : `#${label}`
}

function isQuickFilterActive(filter: QuickFilter): boolean {
    const currentValue = props.modelValue[filter.key]
    if (Array.isArray(filter.value)) {
        return Array.isArray(currentValue) && filter.value.every(v => currentValue.includes(v))
    }
    return Array.isArray(currentValue) ? currentValue.includes(filter.value) : currentValue === filter.value
}

function toggleQuickFilter(filter: QuickFilter) {
    const currentValue = props.modelValue[filter.key] || []
    const currentArray = Array.isArray(currentValue) ? currentValue : [currentValue].filter(Boolean)

    let newValue: string[]
    if (Array.isArray(filter.value)) {
        if (filter.value.every(v => currentArray.includes(v))) {
            newValue = currentArray.filter(v => !filter.value.includes(v))
        } else {
            newValue = [...new Set([...currentArray, ...filter.value])]
        }
    } else {
        if (currentArray.includes(filter.value)) {
            newValue = currentArray.filter(v => v !== filter.value)
        } else {
            newValue = [...currentArray, filter.value]
        }
    }

    updateFilter(filter.key, newValue.length > 0 ? newValue : undefined)
}

function updateFilter(key: string, value: any) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function clearFilter(key: string, value: string) {
    const currentValue = props.modelValue[key]
    if (Array.isArray(currentValue)) {
        const newValue = currentValue.filter(v => v !== value)
        updateFilter(key, newValue.length > 0 ? newValue : undefined)
    } else {
        updateFilter(key, undefined)
    }
}

function resetAll() {
    searchQuery.value = ''
    filterSearchQueries.value = {}
    emit('update:modelValue', {})
}

function applySuggestion(suggestion: SearchSuggestion) {
    searchQuery.value = suggestion.query
    emit('search', suggestion.query)
}

// Filter helpers
function getFilterValue(key: string): (string | number)[] {
    const value = props.modelValue[key]
    return Array.isArray(value) ? value : value ? [value] : []
}

function getFilteredOptions(key: string) {
    const options = props.filterOptions[key] || []
    const query = filterSearchQueries.value[key]
    if (!query) return options
    return matchSorter(options, query, { keys: ['label'] })
}

function getKeywordOptions(key: string) {
    const options = props.filterOptions[key] || []

    return options.map((option: any) => {
        if (typeof option === 'string' || typeof option === 'number') {
            return { value: option, label: String(option) }
        }

        if (option && typeof option === 'object') {
            if ('value' in option && 'label' in option) {
                return {
                    value: option.value,
                    label: option.label,
                    ...(typeof option.disabled === 'boolean' ? { disabled: option.disabled } : {})
                }
            }

            if ('id' in option && 'label' in option) {
                return {
                    value: option.id,
                    label: option.label,
                    ...(typeof option.disabled === 'boolean' ? { disabled: option.disabled } : {})
                }
            }
        }

        return { value: String(option), label: String(option) }
    })
}

function isDropdownSection(section: FilterSection) {
    return !section.displayAs || section.displayAs === 'dropdown' || section.displayAs === 'keywords'
}

</script>
